import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Rocket, Handshake, Calendar, Sparkles } from "lucide-react";

const BOWER_LOGO = "https://customer-assets.emergentagent.com/job_9905ee30-c002-464f-b890-465ab6cc70dc/artifacts/9497g015_Bower%20School%20Logo-01.png";

const pages = [
  {
    title: "UG BBA Programme",
    subtitle: "3-Year Undergraduate in Entrepreneurship",
    description: "India's only 3-year BBA built entirely around entrepreneurship. Practitioner-led teaching, startup incubation, 100+ mentors at Bower School, Hyderabad.",
    path: "/ug",
    icon: Sparkles,
    color: "#0f0f3d",
    accent: "#4242FF",
    badge: "New",
  },
  {
    title: "BEAT",
    subtitle: "Bower Entrepreneurial Aptitude Test",
    description: "India's flagship entrepreneurship assessment for high school students. Discover your entrepreneurial potential.",
    path: "/beat",
    icon: Rocket,
    color: "#1a1a2e",
    accent: "#e94560",
  },
  {
    title: "GITAM x Bower BBA",
    subtitle: "BBA in Entrepreneurship",
    description: "A 4-year undergraduate programme at GITAM Hyderabad. 60 seats. Academics meets venture building.",
    path: "/gitam",
    icon: GraduationCap,
    color: "#004c54",
    accent: "#00b4d8",
  },
  {
    title: "SEED Partnerships",
    subtitle: "B2B School Partnerships",
    description: "Bring entrepreneurship education to your school. Partner with Bower to build the next generation of founders.",
    path: "/seed",
    icon: Handshake,
    color: "#2d3436",
    accent: "#6c5ce7",
  },
  {
    title: "Events",
    subtitle: "Workshops & Bootcamps",
    description: "Explore upcoming entrepreneurship events, workshops, and bootcamps across India.",
    path: "/events",
    icon: Calendar,
    color: "#1e3a5f",
    accent: "#f39c12",
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#fafafa]" style={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }} data-testid="home-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <img src={BOWER_LOGO} alt="Bower" className="h-10 md:h-12" data-testid="home-logo" />
          <a
            href="https://bowerschool.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors"
          >
            bowerschool.com
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 mb-4" data-testid="home-heading">
            Building Entrepreneurs
          </h1>
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto">
            Explore Bower&rsquo;s programmes, assessments, and partnerships designed to nurture the next generation of founders and innovators.
          </p>
        </div>
      </section>

      {/* Page Cards */}
      <section className="pb-24 px-6" data-testid="home-page-links">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
          {pages.map((p) => (
            <Link
              key={p.path}
              to={p.path}
              className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              data-testid={`home-link-${p.path.replace("/", "")}`}
            >
              <div className="p-7 md:p-8">
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: p.accent + "18" }}
                  >
                    <p.icon size={24} style={{ color: p.accent }} />
                  </div>
                  <div className="flex items-center gap-2">
                    {p.badge && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white" style={{ background: p.accent }} data-testid={`home-badge-${p.path.replace("/", "")}`}>
                        {p.badge}
                      </span>
                    )}
                    <ArrowRight
                      size={20}
                      className="text-gray-300 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-300"
                    />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">{p.title}</h2>
                <p className="text-sm font-medium mb-3" style={{ color: p.accent }}>
                  {p.subtitle}
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">{p.description}</p>
              </div>
              <div
                className="h-1 w-0 group-hover:w-full transition-all duration-500"
                style={{ background: p.accent }}
              />
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-400">
            &copy; {new Date().getFullYear()} Bower Educational Services. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Privacy Policy
            </a>
            <a href="https://bowerschool.com" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Website
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
